// 定义雷达坐标系中的矩形接口
export interface RadarRect {
  x_cm_start: number
  x_cm_stop: number
  y_cm_start: number
  y_cm_stop: number
}

// 定义画布坐标系中的矩形接口
export interface CanvasRect {
  left: number
  top: number
  width: number
  height: number
}

// 定义雷达在画布中的位置接口
export interface RadarPosition {
  x_radar: number
  y_radar: number
}

// 定义家具在雷达坐标系中的接口
export interface RadarFurniture {
  x: number
  y: number
  width: number
  height: number
}

/**
 * 检测区域、子区域 矩形转换 雷达坐标系 --> 画布坐标系
 * @param src_rect - 雷达坐标系矩形
 * @param p_radar - 画布中雷达的位置
 * @returns CSS 可用矩形 { left, top, width, height }
 */
export function convert_region_r2c(src_rect: RadarRect, p_radar: RadarPosition): CanvasRect {
  const x_radar = p_radar.x_radar
  const y_radar = p_radar.y_radar

  const left = x_radar + src_rect.x_cm_start
  const top = y_radar - src_rect.y_cm_stop
  const width = src_rect.x_cm_stop - src_rect.x_cm_start
  const height = src_rect.y_cm_stop - src_rect.y_cm_start

  return { left, top, width, height }
}

/** 500px 基准画布上的检测区域包围盒（含 northAngle 旋转） */
export function getDetectionRegionBoundsInBase(
  coordinates: number[],
  angle = 0,
  canvasSize = 500
): CanvasRect {
  const [xStart, xEnd, yStart, yEnd] = coordinates
  const radar: RadarPosition = {
    x_radar: canvasSize / 2,
    y_radar: canvasSize / 2,
  }

  if (xEnd <= xStart || yEnd <= yStart) {
    return { left: 0, top: 0, width: canvasSize, height: canvasSize }
  }

  const canvasRect = convert_region_r2c(
    {
      x_cm_start: xStart,
      x_cm_stop: xEnd,
      y_cm_start: yStart,
      y_cm_stop: yEnd,
    },
    radar
  )

  return rotateRect_cw(
    canvasRect,
    { x: radar.x_radar, y: radar.y_radar },
    angle
  )
}

/**
 * 检测区域、子区域 矩形转换 画布坐标系 --> 雷达坐标系
 * @param dst_rect - 画布矩形
 * @param p_radar - 雷达在画布坐标系中的位置
 * @returns 雷达坐标矩形
 */
export function convert_region_c2r(dst_rect: CanvasRect, p_radar: RadarPosition): RadarRect {
  const x_radar = p_radar.x_radar
  const y_radar = p_radar.y_radar

  const x_cm_start = dst_rect.left - x_radar
  const x_cm_stop = x_cm_start + dst_rect.width

  const y_cm_stop = y_radar - dst_rect.top
  const y_cm_start = y_cm_stop - dst_rect.height

  return {
    x_cm_start,
    x_cm_stop,
    y_cm_start,
    y_cm_stop,
  }
}

/**
 * 家具 矩形转换 雷达坐标系 --> 画布坐标系
 * @param furniture - 雷达坐标家具对象
 * @param p_radar - 雷达在画布上的坐标
 * @returns CSS 可用家具矩形 {left, top, width, height}
 */
export function convert_furniture_r2c(
  furniture: RadarFurniture,
  p_radar: RadarPosition
): CanvasRect {
  const x_radar = p_radar.x_radar
  const y_radar = p_radar.y_radar

  const left = x_radar + furniture.x
  const top = y_radar - furniture.y
  const width = furniture.width
  const height = furniture.height

  return {
    left,
    top,
    width,
    height,
  }
}

/**
 * 家具 矩形转换 画布坐标系 --> 雷达坐标系
 * @param furniture_item - 画布矩形（CSS）
 * @param p_radar - 雷达在画布上的坐标
 * @returns 雷达坐标家具矩形 { x, y, width, height }
 */
export function convert_furniture_c2r(
  furniture_item: CanvasRect,
  p_radar: RadarPosition
): RadarFurniture {
  const x_radar = p_radar.x_radar
  const y_radar = p_radar.y_radar

  const x = furniture_item.left - x_radar
  const y = y_radar - furniture_item.top
  const width = furniture_item.width
  const height = furniture_item.height

  return {
    x,
    y,
    width,
    height,
  }
}

/* =====================  旋转方法 =================================== */
interface Rect {
  left: number
  top: number
  width: number
  height: number
}

interface Point {
  x: number
  y: number
}

/**
 * 顺时针旋转矩形
 * @param src_rect - 输入矩形 { left, top, width, height }
 * @param pRadar - 雷达中心坐标 { x, y }
 * @param angle - 顺时针旋转角度 0, 90, 180, 270
 * @returns 旋转后的矩形 { left, top, width, height }
 */
export function rotateRect_cw(src_rect: Rect, pRadar: Point, angle: number): Rect {
  if (![0, 90, 180, 270].includes(angle)) angle = 0

  const { left, top, width, height } = src_rect
  const cx = left + width / 2
  const cy = top + height / 2

  const dx = cx - pRadar.x
  const dy = cy - pRadar.y

  let new_dx: number = 0
  let new_dy: number = 0

  switch (angle) {
    case 0:
      new_dx = dx
      new_dy = dy
      break
    case 90:
      new_dx = -dy
      new_dy = dx
      break
    case 180:
      new_dx = -dx
      new_dy = -dy
      break
    case 270:
      new_dx = dy
      new_dy = -dx
      break
  }

  const new_cx = pRadar.x + new_dx
  const new_cy = pRadar.y + new_dy

  let new_width = width
  let new_height = height
  if (angle === 90 || angle === 270) {
    new_width = height
    new_height = width
  }

  return {
    left: new_cx - new_width / 2,
    top: new_cy - new_height / 2,
    width: new_width,
    height: new_height,
  }
}

/**
 * 逆时针旋转矩形（家具/检测区域）
 * @param src_rect - 输入矩形 { left, top, width, height }
 * @param pRadar - 雷达中心坐标 { x, y }
 * @param angle - 逆时针旋转角度 0, 90, 180, 270
 * @returns 旋转后的矩形 { left, top, width, height }
 */
export function rotateRect_ccw(src_rect: Rect, pRadar: Point, angle: number): Rect {
  if (![0, 90, 180, 270].includes(angle)) angle = 0

  const { left, top, width, height } = src_rect
  const cx = left + width / 2
  const cy = top + height / 2

  const dx = cx - pRadar.x
  const dy = cy - pRadar.y

  let new_dx: number = 0
  let new_dy: number = 0

  switch (angle) {
    case 0:
      new_dx = dx
      new_dy = dy
      break
    case 90:
      new_dx = dy
      new_dy = -dx
      break
    case 180:
      new_dx = -dx
      new_dy = -dy
      break
    case 270:
      new_dx = -dy
      new_dy = dx
      break
  }

  const new_cx = pRadar.x + new_dx
  const new_cy = pRadar.y + new_dy

  let new_width = width
  let new_height = height
  if (angle === 90 || angle === 270) {
    new_width = height
    new_height = width
  }

  return {
    left: new_cx - new_width / 2,
    top: new_cy - new_height / 2,
    width: new_width,
    height: new_height,
  }
}

interface Point {
  x: number
  y: number
}

/**
 * 将雷达坐标系中的点转换到画布坐标系（旋转 + 平移）
 * @param src_point - 雷达坐标系中的点
 * @param pRadar - 雷达在画布坐标系中的坐标
 * @param angle - 雷达顺时针旋转角度（度）
 * @returns 画布坐标系中的点
 */
export function convert_point_r2c(src_point: Point, pRadar: Point, angle: number): Point {
  const rad = (angle * Math.PI) / 180 // 角度转弧度（顺时针为正）
  const cosA = Math.cos(rad)
  const sinA = Math.sin(rad)

  // 顺时针旋转
  const xRot = src_point.x * cosA + src_point.y * sinA
  const yRot = -src_point.x * sinA + src_point.y * cosA

  // 平移到画布坐标系
  return {
    x: pRadar.x + xRot,
    y: pRadar.y - yRot,
  }
}

/**
 * 根据矩形中心点顺时针旋转矩形，并返回旋转后的外接矩形边界框。
 * 与 CSS 的 transform: rotate() 行为一致。
 *
 * @param srcRect - 原始矩形，包含位置和尺寸信息
 * @param selfRotate - 顺时针旋转角度（单位：度）
 * @returns 旋转后的矩形边界框
 */
export function rotateRectByCenter_cw(
  srcRect: { left: number; top: number; width: number; height: number },
  selfRotate: number
): { left: number; top: number; width: number; height: number } {
  const { left, top, width, height } = srcRect

  // 将角度转换为弧度，供三角函数使用
  const rad = (selfRotate * Math.PI) / 180

  // 计算矩形中心点坐标
  const cx = left + width / 2
  const cy = top + height / 2

  // 定义矩形的四个顶点（按顺时针方向）
  const points = [
    { x: left, y: top }, // 左上
    { x: left + width, y: top }, // 右上
    { x: left + width, y: top + height }, // 右下
    { x: left, y: top + height }, // 左下
  ]

  // 对每个顶点进行旋转变换
  const rotatedPoints = points.map((p) => {
    const dx = p.x - cx
    const dy = p.y - cy
    return {
      x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
      y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
    }
  })

  // 提取所有旋转后顶点的 x 和 y 坐标
  const xs = rotatedPoints.map((p) => p.x)
  const ys = rotatedPoints.map((p) => p.y)

  // 计算新的边界框（最小外接矩形）
  const newLeft = Math.min(...xs)
  const newTop = Math.min(...ys)
  const newWidth = Math.max(...xs) - newLeft
  const newHeight = Math.max(...ys) - newTop

  return {
    left: newLeft,
    top: newTop,
    width: newWidth,
    height: newHeight,
  }
}

/**
 * 根据矩形中心点逆时针旋转矩形边界框，恢复原始位置。
 * 与 rotateRectByCenter_cw 相反，用于还原旋转前的矩形。
 *
 * @param rotatedRect - 已旋转后的矩形边界框
 * @param selfRotate - 原始顺时针旋转角度（单位：度）
 * @returns 逆旋转后的矩形边界框（即原始矩形）
 */
export function rotateRectByCenter_ccw(
  rotatedRect: { left: number; top: number; width: number; height: number },
  selfRotate: number
): { left: number; top: number; width: number; height: number } {
  const { left, top, width, height } = rotatedRect

  // 将角度转换为弧度，逆时针旋转 = 顺时针旋转的负角度
  const rad = (-selfRotate * Math.PI) / 180

  // 计算旋转后的矩形中心点
  const cx = left + width / 2
  const cy = top + height / 2

  // 定义旋转后矩形的四个顶点（顺时针）
  const points = [
    { x: left, y: top }, // 左上
    { x: left + width, y: top }, // 右上
    { x: left + width, y: top + height }, // 右下
    { x: left, y: top + height }, // 左下
  ]

  // 对每个顶点进行逆旋转变换
  const restoredPoints = points.map((p) => {
    const dx = p.x - cx
    const dy = p.y - cy
    return {
      x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
      y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
    }
  })

  // 提取所有逆旋转后顶点的 x 和 y 坐标
  const xs = restoredPoints.map((p) => p.x)
  const ys = restoredPoints.map((p) => p.y)

  // 计算原始边界框（最小外接矩形）
  const originalLeft = Math.min(...xs)
  const originalTop = Math.min(...ys)
  const originalWidth = Math.max(...xs) - originalLeft
  const originalHeight = Math.max(...ys) - originalTop

  return {
    left: originalLeft,
    top: originalTop,
    width: originalWidth,
    height: originalHeight,
  }
}

// 围绕矩形中心点，顺时针旋转矩形，（笛卡尔坐标系）
export function rotateRectSelf_cw_cartesian(
  rect: { left: number; top: number; width: number; height: number },
  angle: number
) {
  if (![0, 90, 180, 270].includes(angle)) angle = 0

  const { left: x, top: y, width, height } = rect

  // 中心点
  const cx = x + width / 2
  const cy = y - height / 2 // 笛卡尔坐标系，y朝上

  let newWidth = width
  let newHeight = height

  if (angle === 90 || angle === 270) {
    newWidth = height
    newHeight = width
  }

  return {
    left: cx - newWidth / 2,
    top: cx ? cy + newHeight / 2 : cy + newHeight / 2, // top = 上边缘坐标
    width: newWidth,
    height: newHeight,
  }
}

// 围绕矩形中心点，逆时针旋转矩形，（笛卡尔坐标系）
export function rotateRectSelf_ccw_cartesian(
  rect: { left: number; top: number; width: number; height: number },
  angle: number
) {
  if (![0, 90, 180, 270].includes(angle)) angle = 0

  const { left: x, top: y, width, height } = rect

  // 中心点
  const cx = x + width / 2
  const cy = y - height / 2 // 笛卡尔坐标系，y朝上

  let newWidth = width
  let newHeight = height

  // 逆时针旋转 90° 或 270° 时，宽高交换
  if (angle === 90 || angle === 270) {
    newWidth = height
    newHeight = width
  }

  return {
    left: cx - newWidth / 2,
    top: cy + newHeight / 2, // top = 上边缘坐标
    width: newWidth,
    height: newHeight,
  }
}

/**
 * 在笛卡尔坐标系（x→右, y→上）中，
 * 将矩形以左上角 (x,y) 表示的 rect 向外扩张 expand 单位。
 * @param {{left:number, top:number, width:number, height:number}} rect  // left,top 为左上角 (minX, maxY)
 * @param {number} expand 扩张距离（正值表示向外扩，负值表示向内收缩）
 * @returns {{left:number, top:number, width:number, height:number}}
 */
export function expandRectCartesian(
  rect: { left: number; top: number; width: number; height: number },
  expand: number
) {
  const { left, top, width, height } = rect

  return {
    left: left - expand,
    top: top + expand,
    width: width + expand * 2,
    height: height + expand * 2,
  }
}
